import { type HeadingDefinition, type ParagraphDefinition } from '@atlaskit/adf-schema';

import type { ParsedStructuredElementType } from '../../layout/types';
import type { TagConverter } from '../types';

/**
 * Turns children (direct string or array that may contain strings) into ADF text content.
 * Returns an empty text node by default.
 */
const childrenToTextContent = (
	children: ParsedStructuredElementType['children'],
): Array<{ text: string; type: 'text' }> => {
	if (typeof children === 'string') {
		return [{ type: 'text', text: children }];
	}
	if (Array.isArray(children)) {
		return children.flatMap((item) =>
			typeof item === 'string' ? [{ type: 'text', text: item }] : [],
		);
	}
	return [{ type: 'text', text: '' }];
};

/**
 * Converts a Lumina heading tag to an ADF heading node.
 * Respects the heading level attribute if provided.
 */
export const convertH: TagConverter = (luminaNode): HeadingDefinition => {
	const content = childrenToTextContent(luminaNode.children);
	const attrs = luminaNode.attributes as Record<string, string | undefined> | undefined;
	return {
		type: 'heading',
		attrs: { level: parseInt(attrs?.level ?? '2') || 2 },
		...(content.length > 0 && { content }),
	};
};

/**
 * Converts a Lumina markdown tag to an ADF paragraph node.
 */
export const convertMd: TagConverter = (luminaNode): ParagraphDefinition => {
	const content = childrenToTextContent(luminaNode.children);
	return {
		type: 'paragraph',
		...(content.length > 0 && { content }),
	};
};
